import _ from 'underscore';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultButton } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { CodeSnippet, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { InsertInstruction } from './insert-instructions';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { endActivity } from '../../shared/util';

const CopyDownloadEnhancedConvTagUnstyled = (props) => {
  const {
    classes,
    instructionsTemplate,
    codeTemplate,
    onCopyCallback,
    onDownloadCallback,
    logActivity,
    wizardDispatch,
  } = props;

  const i18n = useContext(I18nContext);
  const [notification, setNotification] = useState(null);

  const goToEmailTagPage = useCallback(() => {
    endActivity(logActivity);
    wizardDispatch({
      type: WIZARD_ACTIONS.GO_TO_EMAIL_DEVELOPER_STEP,
    });
  }, [logActivity, wizardDispatch]);


  const copyTagToClipboard = useCallback(() => {
    navigator.clipboard.writeText(codeTemplate)
      .then(() => {
        setNotification({ message: i18n.getString(_TL_(('Tag copied!'))) });
        onCopyCallback({
          error: null,
          message: i18n.getString(_TL_(('Tag copied!'))),
        });
      })
      .catch((err) => {
        setNotification({ error: i18n.getString(_TL_(('Tag copy failed!'))) });
        onCopyCallback({
          error: typeof err === 'object' ? JSON.stringify(err) : err,
          message: i18n.getString(_TL_(('Tag copy failed!'))),
        });
      });
  }, [codeTemplate, i18n, onCopyCallback]);

  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notification)) {
      if (notification.message) {
        notificationId = notificationApi.showInfo(notification.message);
      } else if (notification.error) {
        notificationId = notificationApi.showWarning(notification.error);
      }
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notification]);

  const downloadTag = useCallback(() => {
    let htmlContents = '';

    try {
      htmlContents = renderToString((
        <React.Fragment>
          <InsertInstruction
            i18n={i18n}
            template={instructionsTemplate}
            logActivity={logActivity}
            wizardDispatch={wizardDispatch}
          />
          <br />
          <CodeSnippet
            codeTemplate={codeTemplate}
          />
        </React.Fragment>
      ));
      const element = document.createElement('a');

      element.setAttribute('href', `data:text/html;charset=utf-8,${encodeURIComponent(htmlContents)}`);
      element.setAttribute('download', 'Enhaced-Conversions-Tag.html');

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);
      setNotification({ message: i18n.getString(_TL_(('Enhanced Conversions Tag downloaded!'))) });
      onDownloadCallback({
        error: null,
        message: i18n.getString(_TL_(('Enhanced Conversions Tag downloaded!'))),
        tag: htmlContents,
      });
    } catch (err) {
      setNotification({ message: i18n.getString(_TL_(('Enhanced Conversions Tag download failed!'))) });
      onDownloadCallback({
        error: typeof err === 'object' ? JSON.stringify(err) : err,
        message: i18n.getString(_TL_(('Enhanced Conversions Tag download failed!'))),
        tag: htmlContents,
      });
    }
  }, [codeTemplate, i18n, instructionsTemplate, logActivity, onDownloadCallback, wizardDispatch]);

  return (
    <div className={classNames(classes.buttonBlock, 'copy-tag')}>
      <DefaultButton onClick={copyTagToClipboard} text={i18n.getString(_TL_('Copy tag'))} />
      <DefaultButton onClick={downloadTag} text={i18n.getString(_TL_('Download tag'))} />
      <DefaultButton onClick={goToEmailTagPage} text={i18n.getString(_TL_('Send instruction to a developer'))} />
    </div>
  );
};

CopyDownloadEnhancedConvTagUnstyled.propTypes = {
  model: PropTypes.shape({}).isRequired,
  onCopyCallback: PropTypes.func.isRequired,
  onDownloadCallback: PropTypes.func.isRequired,
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  instructionsTemplate: PropTypes.arrayOf(PropTypes.string).isRequired,
  logActivity: PropTypes.shape({}).isRequired,
  wizardDispatch: PropTypes.func.isRequired,
  codeTemplate: PropTypes.string.isRequired,
};

export const CopyDownloadEnhancedConvTag = withDefaultStyles(CopyDownloadEnhancedConvTagUnstyled, getStepStyles);
